<template>
  <div class="admin-login-container">
    <div class="admin-login-shell">
      <section class="admin-login-panel admin-login-panel--story">
        <div class="story-bg"></div>
        <div class="story-overlay"></div>
        <div class="story-content">
          <div class="story-brand">
            <span class="story-brand-mark">
              <icon icon="fa-solid fa-book-open" />
            </span>
            <span>Lối Đọc Admin</span>
          </div>

          <div class="story-copy">
            <p class="story-kicker">Cổng quản trị nội bộ</p>
            <h1>Điều phối thư viện rõ ràng từ lượt đăng nhập đầu tiên.</h1>
            <p>
              Quản lý sách, tác giả, danh mục và nhân sự vận hành trong một không gian tập trung,
              bảo mật và dễ kiểm soát.
            </p>
          </div>

          <div class="story-grid" aria-label="Trạng thái hệ thống">
            <div class="story-stat">
              <span class="story-stat-value">256-bit</span>
              <span class="story-stat-label">Mã hóa phiên</span>
            </div>
            <div class="story-stat">
              <span class="story-stat-value">24/7</span>
              <span class="story-stat-label">Theo dõi truy cập</span>
            </div>
            <div class="story-stat story-stat--wide">
              <span class="story-stat-value">RBAC</span>
              <span class="story-stat-label">Phân quyền theo vai trò quản trị</span>
            </div>
          </div>
        </div>
      </section>

      <section class="admin-login-panel admin-login-panel--form" aria-label="Đăng nhập quản trị">
        <div class="admin-login-content">
          <div class="admin-card">
            <div class="admin-card-header">
              <span class="portal-badge">{{ isOtpStep ? 'Xác thực OTP' : 'Phiên bảo mật' }}</span>
              <h2 class="portal-title">{{ isOtpStep ? 'Nhập mã xác thực' : 'Đăng nhập quản trị' }}</h2>
              <p class="portal-subtitle">
                {{ isOtpStep ? 'Mã OTP đã được gửi đến email của bạn. Vui lòng nhập mã để hoàn tất.' : 'Dành cho quản trị viên và nhân viên được cấp quyền.' }}
              </p>
            </div>

            <div class="portal-access-note" v-if="!isOtpStep">
              <icon icon="fa-solid fa-fingerprint" />
              <span>Thiết bị đăng nhập sẽ được ghi nhận để bảo vệ phiên làm việc.</span>
            </div>

            <!-- Standard Login Form -->
            <form v-if="!isOtpStep" @submit.prevent="handleLogin" novalidate class="portal-form">
              <div class="portal-form-group">
                <label class="portal-label">Tên đăng nhập</label>
                <div class="portal-input-wrapper">
                  <input
                    v-model.trim="username"
                    type="text"
                    class="portal-input"
                    :class="{ 'is-invalid': errors.username }"
                    placeholder="Nhập tên đăng nhập"
                    autocomplete="username"
                    @input="clearError('username')"
                  />
                  <icon icon="fa-solid fa-user" class="portal-input-icon" />
                </div>
                <div v-if="errors.username" class="portal-error-message">
                  {{ errors.username }}
                </div>
              </div>

              <div class="portal-form-group">
                <label class="portal-label">Mật khẩu</label>
                <div class="portal-input-wrapper">
                  <input
                    v-model="password"
                    :type="showPassword ? 'text' : 'password'"
                    class="portal-input"
                    :class="{ 'is-invalid': errors.password }"
                    placeholder="Nhập mật khẩu"
                    autocomplete="current-password"
                    @input="clearError('password')"
                  />
                  <icon icon="fa-solid fa-lock" class="portal-input-icon" />
                  <button
                    type="button"
                    class="portal-input-eye"
                    :aria-label="showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'"
                    @click="togglePassword"
                  >
                    <icon :icon="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'" />
                  </button>
                </div>
                <div v-if="errors.password" class="portal-error-message">
                  {{ errors.password }}
                </div>
              </div>

              <div class="portal-meta-row">
                <label class="portal-checkbox-label">
                  <input
                    v-model="rememberMe"
                    type="checkbox"
                    class="portal-checkbox"
                  />
                  <span class="portal-checkbox-text">Ghi nhớ đăng nhập</span>
                </label>
                <span class="portal-session-tag">SSL active</span>
              </div>

              <button
                type="submit"
                class="portal-btn-primary"
                :disabled="isLoading"
              >
                <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span>{{ isLoading ? 'Đang xác thực...' : 'Đăng nhập vào hệ thống' }}</span>
                <span class="portal-btn-icon-wrapper" v-if="!isLoading">
                  <icon icon="fa-solid fa-arrow-right" class="portal-btn-icon" />
                </span>
              </button>
            </form>

            <!-- OTP Verification Form -->
            <form v-else @submit.prevent="handleVerifyOtp" novalidate class="portal-form">
              <div class="portal-form-group">
                <label class="portal-label">Mã OTP (6 chữ số)</label>
                <div class="portal-input-wrapper">
                  <input
                    v-model.trim="otpCode"
                    type="text"
                    class="portal-input portal-input-otp"
                    :class="{ 'is-invalid': errors.otp }"
                    placeholder="------"
                    maxlength="6"
                    @input="clearError('otp')"
                  />
                  <icon icon="fa-solid fa-key" class="portal-input-icon" />
                </div>
                <div v-if="errors.otp" class="portal-error-message">
                  {{ errors.otp }}
                </div>
              </div>

              <button
                type="submit"
                class="portal-btn-primary"
                :disabled="isOtpLoading"
              >
                <span v-if="isOtpLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span>{{ isOtpLoading ? 'Đang xác thực...' : 'Xác thực OTP' }}</span>
                <span class="portal-btn-icon-wrapper" v-if="!isOtpLoading">
                  <icon icon="fa-solid fa-check" class="portal-btn-icon" />
                </span>
              </button>

              <div class="otp-resend-wrapper">
                <div v-if="otpCountdown > 0" class="otp-timer-text">
                  Mã có hiệu lực trong: <span class="otp-timer-highlight">{{ formattedCountdown }}</span>
                </div>
                <div v-else class="otp-resend-text">
                  <span>Không nhận được mã? </span>
                  <button type="button" @click="handleResendOtp" class="portal-link-btn" :disabled="isResendingOtp">
                    <span v-if="isResendingOtp" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    <span>Gửi lại mã OTP</span>
                  </button>
                </div>
              </div>

              <div class="otp-back-wrapper">
                <button type="button" @click="isOtpStep = false" class="portal-back-btn">
                  <icon icon="fa-solid fa-arrow-left" class="me-1" />
                  <span>Quay lại đăng nhập</span>
                </button>
              </div>
            </form>

            <div class="admin-portal-footer">
              <p class="footer-security-text">
                <icon icon="fa-solid fa-shield-halved" class="portal-security-icon" />
                Hệ thống giám sát bảo mật và phân quyền truy cập.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script src="./login.js"></script>
<style scoped src="./login.css"></style>
